import React from "react";
import { Route, Routes, useLocation } from "react-router-dom";

// Language & Routing
import LanguageLayout from "./layout/LanguageLayout";
import RootRedirect from "./components/routing/RootRedirect";

// Start Main
import Header from "./components/main/Header";
import Footer from "./components/main/Footer";

// Auth Pages
import Register from "./components/auth/Register";
import Login from "./components/auth/Login";
import ForgotPassword from "./components/auth/ForgotPassword";
import VerifyOTP from "./components/auth/VerifyOTP";
import RegisterComplete from "./components/auth/RegisterComplete";
import RegisterCancelled from "./components/auth/RegisterCancelled";

// User Pages
import Profile from "./components/admin/Profile";

// Start Pages
import Home from "./components/pages/Home";
import AboutUs from "./components/pages/AboutUs";
import Blog from "./components/pages/Blog";
import ContactUs from "./components/pages/ContactUs";
import Forms from "./components/pages/Forms";
import FormsDetails from "./components/pages/FormsDetails";
import FormPackageDetails from "./components/pages/FormPackageDetails";
import PrivacyPolicy from "./components/pages/PrivacyPolicy";
import TermsOfService from "./components/pages/TermsOfService";
import Disclaimer from "./components/pages/Disclaimer";
import Faqs from "./components/pages/Faqs";
import Services from "./components/pages/Services";
import Checkout from "./components/pages/Checkout";
import NotFound from "./components/pages/NotFound";
import CRMPage from "./components/pages/CRMPage";
import AttorneysPage from "./components/pages/AttorneysPage";
import Chatbot from "./components/pages/Chatbot";
import PaymentSuccess from "./components/pages/PaymentSuccess";
import PaymentCancel from "./components/pages/PaymentCancel";
import LawyerAi from "./components/pages/LawyerAi";
import SubCategory from "./components/pages/SubCategory";
import GreenCard from "./components/pages/GreenCard";
import Citizenship from "./components/pages/Citizenship";
import AdjustmentOfStatus from "./components/pages/AdjustmentOfStatus";
import FromServices from "./components/pages/FromServices";
import BlogDetails from "./components/pages/BlogDetails";
import PackagePaymentStatus from "./components/pages/PackagePaymentStatus";

import {
  FormAssistance,
  LawyerMatching,
  PaymentProcessing,
  LegalConsultation,
  DocumentReview,
  CaseTracking,
  ImmigrationSupport,
  LegalRepresentation,
  OnlineConsultation,
} from "./components/pages/services-pages";

// Start Admin
import ParalegalProfile from "./components/admin/rule/ParalegalProfile";
import ClientProfile from "./components/admin/rule/ClientProfile";
import LawyerProfile from "./components/admin/rule/LawyerProfile";
import OfficeOwnerProfile from "./components/admin/rule/OfficeOwnerProfile";

// Start Ui
import CookieConsent from "./components/ui/CookieConsent";

// Start Hooks
import Top from "./components/Top";
import ScrollToTop from "./components/ScrollToTop";
import UseScrollAnimation from "./hooks/UseScrollAnimation";

const App = () => {
  const location = useLocation();

  // Check if current route is admin or AI chat (these routes don't use language prefix)
  const isAdminRoute = location.pathname.startsWith("/profile/");
  const isAiChatRoute = location.pathname.includes("/lawyer-ai");

  // Check if current route has language prefix
  const hasLangPrefix = location.pathname.match(/^\/[a-z]{2}(\/|$)/);

  return (
    <>
      {!isAdminRoute && !isAiChatRoute && hasLangPrefix && <Header />}
      <CookieConsent />
      <Top />

      <Routes>
        {/* Root redirect - redirect to default language */}
        <Route path="/" element={<RootRedirect />} />

        {/* Language-aware routes - all main routes wrapped in LanguageLayout */}
        <Route path="/:lang" element={<LanguageLayout />}>
          {/* Main Pages */}
          <Route index element={<Home />} />
          <Route path="about" element={<AboutUs />} />
          <Route path="blog" element={<Blog />} />
          <Route path="blog/:id" element={<BlogDetails />} />
          <Route path="contact" element={<ContactUs />} />
          <Route path="forms" element={<Forms />} />
          <Route path="forms/:code" element={<FormsDetails />} />
          <Route path="form-packages/:package_id" element={<FormPackageDetails />} />
          <Route path="privacy-policy" element={<PrivacyPolicy />} />
          <Route path="terms-of-service" element={<TermsOfService />} />
          <Route path="disclaimer" element={<Disclaimer />} />
          <Route path="faq" element={<Faqs />} />
          <Route path="payment/success" element={<PaymentSuccess />} />
          <Route path="payment/cancel" element={<PaymentCancel />} />
          <Route path="services" element={<Services />} />
          <Route path="checkout/:responseId" element={<Checkout />} />
          <Route path="crm" element={<CRMPage />} />
          <Route path="lawyer" element={<AttorneysPage />} />
          <Route path="sub-category" element={<SubCategory />} />

          <Route path="green-card" element={<GreenCard />} />
          <Route path="green-card/:slug" element={<GreenCard />} />

          <Route path="citizenship" element={<Citizenship />} />
          <Route path="citizenship/:slug" element={<Citizenship />} />

          <Route path="adjustment-of-status" element={<AdjustmentOfStatus />} />
          <Route path="adjustment-of-status/:slug" element={<AdjustmentOfStatus />} />

          <Route path="from-services" element={<FromServices />} />
          <Route path="from-services/:slug" element={<FromServices />} />

          {/* Package Payment Routes */}
          <Route path="packages/:packageId/payment/:status" element={<PackagePaymentStatus />} />

          {/* Services Sub-Pages */}
          <Route path="services/form-assistance" element={<FormAssistance />} />
          <Route path="services/lawyer-matching" element={<LawyerMatching />} />
          <Route path="services/payment-processing" element={<PaymentProcessing />} />
          <Route path="services/legal-consultation" element={<LegalConsultation />} />
          <Route path="services/document-review" element={<DocumentReview />} />
          <Route path="services/case-tracking" element={<CaseTracking />} />
          <Route path="services/immigration-support" element={<ImmigrationSupport />} />
          <Route path="services/legal-representation" element={<LegalRepresentation />} />

          <Route path="services/online-consultation" element={<OnlineConsultation />} />

          {/* AI Chat Page - localized */}
          <Route path="lawyer-ai" element={<LawyerAi />} />
          {/* Auth Pages */}
          <Route path="register" element={<Register />} />
          <Route path="login" element={<Login />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="verify-otp" element={<VerifyOTP />} />
          <Route path="register/complete" element={<RegisterComplete />} />
          <Route path="register/cancelled" element={<RegisterCancelled />} />
        </Route>

        {/* AI Chat Page - root fallback */}
        <Route path="/lawyer-ai" element={<LawyerAi />} />

        {/* Admin Pages - without language prefix */}
        <Route path="/profile" element={<Profile />} />
        <Route path="/profile/office" element={<OfficeOwnerProfile />} />
        <Route path="/profile/lawyer" element={<LawyerProfile />} />
        <Route path="/profile/client" element={<ClientProfile />} />
        <Route path="/profile/paralegal" element={<ParalegalProfile />} />

        {/* 404 - Catch all */}
        <Route path="/register/complete" element={<RootRedirect />} />
        <Route path="*" element={<NotFound />} />
      </Routes>

      {!isAdminRoute && !isAiChatRoute && hasLangPrefix && <Chatbot />}

      {!isAdminRoute && !isAiChatRoute && hasLangPrefix && <Footer />}
      <ScrollToTop />
      <UseScrollAnimation />
    </>
  );
};

export default App;